import { useExternalScheduleForm } from '@forms/schedules/external';
import { useToast } from '@hooks/useToast';
import { useTranslation } from 'react-i18next';
import { ExternalScheduleDialog } from '..';
import { useEffect, useRef, useState } from 'react';
import { Tabs } from '@components/ui/tabs';
import { options } from './constants';
import { useGetJobConcreteDirect } from '@services/jobs/concreteDirects';
import { Loader } from '@components/loader';
import { useDropdownsContext } from '@contexts/dropdowns';
import { DefaultExternalScheduleDialogProps } from '../types';
import { ExternalScheduleOrderDialogOption } from './types';
import { ExternalScheduleDateInputs } from '../inputs/date';
import { ExternalScheduleMaterialInputs } from '../inputs/material';
import { ExternalScheduleConcreteDirectInputs } from '../inputs/concreteDirect';
import { ExternalScheduleContactsInputs } from '../inputs/contacts';
import { useRescheduler } from '@contexts/rescheduler';
import { defaultValues, getMaterialOptionsValues } from '@forms/schedules/external/constants';
import { useAppContext } from '@contexts/app';
import { isIntegrationEnabled } from '@utils/integrations';

export default function ExternalScheduleOrderDialog({
  open,
  setOpen,
  name,
  taskId,
  schedule,
  completed,
  importantTasks,
  jobId,
  concrete,
  note,
  concreteDirectId,
  address,
  geolocalization,
  additionalInputs,
  extraComment,
  readOnly,
  scheduleType,
  phoneLayout,
  takenDays,
}: DefaultExternalScheduleDialogProps) {
  const { concreteMix, additive, setParams } = useDropdownsContext();
  const { t } = useTranslation('app');
  const { toast } = useToast();
  const rescheduler = useRescheduler();
  const { tenant } = useAppContext();
  const concreteDirectEnabled = isIntegrationEnabled(tenant, 'concreteDirect');
  const [option, setOption] = useState<ExternalScheduleOrderDialogOption>(
    concreteDirectId ? 'concreteDirectOrder' : 'manualConcreteOrder',
  );

  const { data, isLoading } = useGetJobConcreteDirect(open, jobId, concreteDirectId);

  const { form, onSubmit, locked } = useExternalScheduleForm({
    concreteDirect: option === 'concreteDirectOrder',
    schedule,
    jobId,
    taskId,
    scheduleId: schedule?.id,
    note,
    successHandler: (resp) => {
      form.reset();
      setOpen(false);
      toast({
        title: t(`messages.success.schedule.${schedule?.id ? 'update' : 'create'}`),
      });
      if (!resp || !rescheduler) return;
      const { data: updatedSchedule } = resp;
      rescheduler.appendSavedSchedule(updatedSchedule);
      rescheduler.setState((prev) => ({
        ...prev,
        relatedSchedules: prev.relatedSchedules.map((el) =>
          el.task?.id === updatedSchedule.task?.id ? updatedSchedule : el,
        ),
      }));
    },
  });

  const { reset, getValues, watch } = form;
  const siteId = watch('externalSiteId');
  const materialInitializedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!open) {
      materialInitializedFor.current = null;
      return;
    }
    const key = `${taskId}-${concrete?.id}`;
    if (materialInitializedFor.current === key) return;
    materialInitializedFor.current = key;
    reset({ ...getValues(), materialOptions: getMaterialOptionsValues(concrete?.options) });
  }, [open, taskId, concrete, reset, getValues]);

  useEffect(() => {
    setParams({ products: { externalSiteId: siteId } });
  }, [setParams, siteId]);

  useEffect(() => {
    if (!open && !concreteDirectId) {
      reset(defaultValues);
      return;
    }
    if (!data?.data) return;
    const {
      externalSiteId,
      itemId,
      quantity,
      isExactQuantity,
      slump,
      orderItemAdditions: { itemId: additiveId, quantity: additiveQuantity },
      pourMethod,
      pourElement,
      spacingInMinutes,
      qtyPerHour,
      rateType,
      additionalInformation,
    } = data.data;

    reset({
      ...getValues(),
      externalSiteId,
      id: [itemId],
      quantity,
      isExactQuantity,
      slump,
      additive: {
        id: [additiveId],
        quantity: additiveQuantity,
      },
      pourMethod,
      pourElement,
      quantityRate: spacingInMinutes || qtyPerHour || '',
      rateType,
      additionalInformation,
    });
  }, [open, getValues, concreteDirectId, reset, data]);

  return (
    <ExternalScheduleDialog
      type={option}
      open={open}
      setOpen={setOpen}
      completed={completed}
      form={form}
      onSubmit={onSubmit}
      locked={locked}
      jobId={jobId}
      name={name}
      taskId={taskId}
      schedule={schedule}
      note={note}
      extraComment={extraComment}
      concreteDirectId={concreteDirectId}
      importantTasks={importantTasks}
      readOnly={readOnly}
      scheduleType={scheduleType}
      phoneLayout={phoneLayout}
      takenDays={takenDays}
      whenDisabled={
        completed || readOnly || (option === 'concreteDirectOrder' && Boolean(concreteDirectId))
      }>
      {concreteDirectEnabled && (
        <Tabs
          value={option}
          setValue={setOption}
          disabled={Boolean(schedule?.id)}
          options={options}
        />
      )}
      {option === 'manualConcreteOrder' && (
        <>
          {!phoneLayout && <ExternalScheduleDateInputs disabled={completed || readOnly} />}
          {additionalInputs && (
            <ExternalScheduleContactsInputs
              disabled={readOnly}
              open={open}
              jobId={jobId}
              initialContactId={schedule?.contact?.id}
            />
          )}
          <ExternalScheduleMaterialInputs
            material={concrete}
            editable
            disabled={completed || readOnly}
          />
        </>
      )}
      {option === 'concreteDirectOrder' &&
        (concreteDirectId && (isLoading || concreteMix.disabled || additive.disabled) ? (
          <div className="relative h-8">
            <Loader local />
          </div>
        ) : (
          <ExternalScheduleConcreteDirectInputs
            disabled={completed || Boolean(concreteDirectId) || readOnly}
            address={address}
            geolocalization={geolocalization}>
            {!phoneLayout && (
              <ExternalScheduleDateInputs
                disabled={completed || Boolean(concreteDirectId) || readOnly}
              />
            )}
            <ExternalScheduleMaterialInputs material={concrete} />
          </ExternalScheduleConcreteDirectInputs>
        ))}
    </ExternalScheduleDialog>
  );
}
